import React, { useState, useEffect } from 'react';
import ContactCard from '../components/contacts/ContactCard';
import { contactsService } from '../services/contactsService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AddEditContactModal from '../components/contacts/AddEditContactModal';
import ImportCsvModal from '../components/common/ImportCsvModal';
import { LuUsers, LuSearch, LuPlus, LuUpload } from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const result = await contactsService.getContactsWithAccount();

      if (!result.success) {
        setError(result.message);
        setContacts([]);
      } else {
        setContacts(result.data);
        setError('');
      }
    } catch (err) {
      setError('Failed to fetch contacts');
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
    const email = contact.email?.toLowerCase() || '';
    const accountName = contact.accounts?.name?.toLowerCase() || '';

    return fullName.includes(searchLower) ||
      email.includes(searchLower) ||
      accountName.includes(searchLower) ||
      (contact.job_title && contact.job_title.toLowerCase().includes(searchLower));
  });

  const handleAdd = () => {
    setContactToEdit(null);
    setIsModalOpen(true);
  };

  const handleEdit = (contact) => {
    setContactToEdit(contact);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        const result = await contactsService.delete(id);
        if (result.success) {
          fetchContacts();
        } else {
          setError(result.message);
        }
      } catch (err) {
        console.error('Error deleting contact:', err);
        setError('Failed to delete contact');
      }
    }
  };

  const handleModalSave = () => {
    fetchContacts();
    setIsModalOpen(false);
  };

  const handleImportComplete = (results) => {
    // Refresh contacts list after import
    fetchContacts();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Contacts</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage your customer contacts with privacy controls
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <SafeIcon icon={LuUpload} className="-ml-1 mr-2 h-5 w-5" />
            Import CSV
          </button>
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <SafeIcon icon={LuPlus} className="-ml-1 mr-2 h-5 w-5" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="relative">
          <SafeIcon
            icon={LuSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search contacts by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="large" />
        </div>
      ) : (
        <>
          {/* Contact Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <SafeIcon icon={LuUsers} className="h-8 w-8 text-blue-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Contacts</p>
                  <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-yellow-500 rounded-md"></div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">VIP Contacts</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {contacts.filter(c => c.is_private).length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-green-500 rounded-md"></div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Companies</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {new Set(contacts.map(c => c.accounts?.name).filter(Boolean)).size}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>

          {/* Empty State */}
          {filteredContacts.length === 0 && (
            <div className="text-center py-12">
              <SafeIcon icon={LuUsers} className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {searchTerm ? 'No matching contacts found' : 'No contacts yet'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Get started by adding your first contact'
                }
              </p>
            </div>
          )}
        </>
      )}

      <AddEditContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleModalSave}
        contactToEdit={contactToEdit}
      />

      <ImportCsvModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        entityType="contacts"
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default Contacts;